import { formatTime } from '@components/AudioPlayer/components/ProgressBar/helpers/formatTime';
import { GetJobUtterancesResponseDto } from 'app/API';
import { intervalToDuration } from 'date-fns';
import { Fragment, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { flattenUtteranceWords } from '../../helpers/flattenUtteranceWords';
import { formatSpeakerLabel } from '../../helpers/formatSpeakerLabel';
import { getSpeakerIndexLabel } from '../../helpers/getSpeakerIndexLabel';
import { useTranscriptSearch } from '../../hooks/useTranscriptSearch';
import type { TSearchHighlight } from '../../types/TSearch';
import { TranscriptSearchBox } from '../TranscriptSearchBox';
import { TranscriptSearchHighlight } from '../TranscriptSearchHighlight';
import { TProps } from './types/TProps';

type Utterance = GetJobUtterancesResponseDto['utterances'][number];

const convertSecondsToTime = (seconds: number) => {
  const { hours, minutes, seconds: sec } = intervalToDuration({ start: 0, end: seconds });
  return `${String(hours || 0).padStart(2, '0')}:${String(minutes || 0).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

type UtteranceRowProps = {
  utterance: Utterance;
  speakerNameLength: number;
  activeWordIdx: number;
  onWordClick: (timestamp: number) => void;
  highlights?: Map<number, TSearchHighlight[]>;
  currentMatchIndex: number;
};

const UtteranceRow = memo(
  ({ utterance, speakerNameLength, activeWordIdx, onWordClick, highlights, currentMatchIndex }: UtteranceRowProps) => (
    <div className="flex min-w-0 flex-col md:flex-row">
      <span
        className="flex-shrink-0 min-w-0 max-w-full inline-block mr-4 font-mono whitespace-normal md:whitespace-nowrap"
        style={{ width: `${speakerNameLength + 12}ch` }}
      >
        <span>{convertSecondsToTime(utterance.start)}</span> | <span>{formatSpeakerLabel(utterance.speaker)}:</span>
      </span>
      <span className={`confidence-${utterance.confidence} inline-block`}>
        {utterance.words.map((word, wordIdx) => {
          const isActive = wordIdx === activeWordIdx;
          return (
            <Fragment key={wordIdx}>
              <button
                type="button"
                className={`word inline-block border-0 p-0 text-left ${
                  isActive ? 'bg-[var(--color-accent)]' : 'bg-transparent'
                }`}
                onClick={() => onWordClick(word.start)}
                title={formatTime(word.start / 1000)}
              >
                <TranscriptSearchHighlight
                  text={word.text}
                  highlights={highlights?.get(wordIdx)}
                  currentMatchIndex={currentMatchIndex}
                />
              </button>
              &nbsp;
            </Fragment>
          );
        })}
      </span>
    </div>
  ),
);

UtteranceRow.displayName = 'UtteranceRow';

export const TranscriptLayout = ({
  utterances,
  playbackTime,
  onWordClick,
  searchNamePrefix,
  searchFocusSignal,
  searchFocusEnabled = true,
  onSearchActivate,
  className = 'mt-[var(--transcript-toolbar-clearance,6rem)]',
  title,
  showSpeakers = true,
  autoScrollActiveWord = false,
  height,
}: TProps) => {
  const currentTimeMs = playbackTime * 1000;
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const searchableWords = useMemo(() => flattenUtteranceWords(utterances), [utterances]);
  const {
    query,
    setQuery,
    caseSensitive,
    setCaseSensitive,
    wholeWord,
    setWholeWord,
    matchCount,
    currentMatchIndex,
    currentMatchNumber,
    highlightMap,
    currentMatch,
    scrollTarget,
    goToNextMatch,
    goToPreviousMatch,
  } = useTranscriptSearch({ words: searchableWords });

  const highlightsByUtterance = useMemo(() => {
    const result = new Map<number, Map<number, TSearchHighlight[]>>();

    highlightMap.forEach((highlights, key) => {
      const [utteranceIndexText, wordIndexText] = key.split(':');
      const utteranceIndex = Number(utteranceIndexText);
      const wordIndex = Number(wordIndexText);
      const wordHighlights = result.get(utteranceIndex) ?? new Map<number, TSearchHighlight[]>();

      wordHighlights.set(wordIndex, highlights);
      result.set(utteranceIndex, wordHighlights);
    });

    return result;
  }, [highlightMap]);

  const currentMatchItemIndexes = useMemo(
    () => new Set<number>(currentMatch?.spans.map((span) => span.itemIndex) ?? []),
    [currentMatch],
  );

  const speakers = useMemo(() => Array.from(new Set(utterances.map((utterance) => utterance.speaker))), [utterances]);

  const speakerNameLength = useMemo(() => {
    return speakers.reduce((longest, speaker) => Math.max(longest, formatSpeakerLabel(speaker).length), 0);
  }, [speakers]);

  const activeLocation = useMemo(() => {
    for (let utteranceIdx = 0; utteranceIdx < utterances.length; utteranceIdx++) {
      const utterance = utterances[utteranceIdx];

      for (let wordIdx = 0; wordIdx < utterance.words.length; wordIdx++) {
        const word = utterance.words[wordIdx];
        const nextWord = utterance.words[wordIdx + 1];
        const nextWordStart =
          nextWord?.start ??
          utterance.end ??
          word.end ??
          utterances[utteranceIdx + 1]?.start ??
          Number.POSITIVE_INFINITY;

        if (currentTimeMs >= word.start && currentTimeMs < nextWordStart) {
          return { utteranceIdx, wordIdx };
        }
      }
    }

    return null;
  }, [currentTimeMs, utterances]);

  const activeUtteranceIdx = activeLocation?.utteranceIdx ?? -1;
  const activeWordIdx = activeLocation?.wordIdx ?? -1;

  useEffect(() => {
    if (!scrollTarget || !virtuosoRef.current) {
      return;
    }

    virtuosoRef.current.scrollToIndex({
      index: scrollTarget.match.itemIndex,
      align: 'center',
      behavior: 'auto',
    });
  }, [scrollTarget]);

  useEffect(() => {
    if (!autoScrollActiveWord || activeUtteranceIdx < 0 || !virtuosoRef.current) {
      return;
    }

    virtuosoRef.current.scrollToIndex({
      index: activeUtteranceIdx,
      align: 'center',
      behavior: 'smooth',
    });
  }, [activeUtteranceIdx, autoScrollActiveWord]);

  const onWordClickRef = useRef(onWordClick);
  onWordClickRef.current = onWordClick;
  const handleWordClick = useCallback((timestamp: number) => onWordClickRef.current(timestamp), []);

  const Header = useMemo(() => {
    const HeaderComponent = () => (
      <>
        {title ? (
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">{title}</h2>
        ) : null}

        {showSpeakers ? (
          <div className="mb-4">
            {speakers.map((speaker, idx) => (
              <div key={speaker}>
                <p className="text-sm">
                  {speaker ? `Speaker ${getSpeakerIndexLabel(idx)}: ${speaker}` : 'Unknown Speaker'}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </>
    );

    return HeaderComponent;
  }, [title, showSpeakers, speakers]);

  const components = useMemo(() => ({ Header }), [Header]);

  const itemContent = useCallback(
    (index: number, utterance: Utterance) => (
      <UtteranceRow
        utterance={utterance}
        speakerNameLength={speakerNameLength}
        activeWordIdx={index === activeUtteranceIdx ? activeWordIdx : -1}
        onWordClick={handleWordClick}
        highlights={highlightsByUtterance.get(index)}
        currentMatchIndex={currentMatchItemIndexes.has(index) ? currentMatchIndex : -1}
      />
    ),
    [
      speakerNameLength,
      activeUtteranceIdx,
      activeWordIdx,
      handleWordClick,
      highlightsByUtterance,
      currentMatchItemIndexes,
      currentMatchIndex,
    ],
  );

  const searchBox = (
    <TranscriptSearchBox
      namePrefix={searchNamePrefix}
      query={query}
      matchCount={matchCount}
      currentMatchNumber={currentMatchNumber}
      caseSensitive={caseSensitive}
      wholeWord={wholeWord}
      focusSignal={searchFocusSignal}
      focusSignalEnabled={searchFocusEnabled}
      className={height ? 'mb-3' : 'sticky top-[var(--transcript-toolbar-bottom,0px)] z-[9] mb-3'}
      onQueryChange={setQuery}
      onCaseSensitiveChange={setCaseSensitive}
      onWholeWordChange={setWholeWord}
      onPrevious={goToPreviousMatch}
      onNext={goToNextMatch}
      onActivate={onSearchActivate}
    />
  );

  if (height) {
    return (
      <div className={className}>
        {searchBox}
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: `calc(${height} - 4.25rem - 2px)` }}
          data={utterances}
          components={components}
          itemContent={itemContent}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      {searchBox}
      <Virtuoso ref={virtuosoRef} useWindowScroll data={utterances} components={components} itemContent={itemContent} />
    </div>
  );
};
